<template>
    <v-app>
      <!-- Top App Bar -->
      <v-app-bar app color="#062f2b" dark dense elevation="2">
        <v-toolbar-title class="font-weight-bold tracking-wide">
          ✈️ Ticket To Ghana
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-chip class="ma-2" color="teal darken-3" text-color="white" small>
          <v-icon left small>mdi-lock-outline</v-icon> Agent Portal
        </v-chip>
      </v-app-bar>
  
      <v-main class="grey lighten-4">
        <v-container class="pa-4" fluid style="max-width: 600px;">
          
          <!-- Search Controls Card -->
          <v-card class="mb-4 rounded-lg" elevation="1">
            <v-card-title class="subtitle-1 font-weight-bold teal--text">
              Flight Search Parameters
            </v-card-title>
            
            <v-card-text class="pt-0">
              <v-row dense>
                <!-- Route Display (Read Only for Context) -->
                <v-col cols="6">
                  <v-select 
                  label="From" 
                  v-model="searchParams.origin"
                  :items="origins"
                  @change="fetchFlights"
                  dense 
                  outlined 
                  prepend-inner-icon="mdi-airplane-takeoff">
                  </v-select>
                  <!-- <v-text-field label="From" value="Johannesburg (JNB)" 
                  dense outlined readonly prepend-inner-icon="mdi-airplane-takeoff"></v-text-field> -->
                </v-col>
                <v-col cols="6">
                  <v-select 
                  label="To" 
                  v-model="searchParams.destination"
                  :items="origins"
                  @change="fetchFlights"
                  dense 
                  outlined 
                  prepend-inner-icon="mdi-airplane-takeoff">
                  </v-select>
                  <!-- <v-text-field label="To" value="Accra (ACC)" dense outlined readonly prepend-inner-icon="mdi-airplane-landing"></v-text-field> -->
                </v-col>
  
                <!-- Cabin Class Selection -->
                <v-col cols="6">
                  <v-menu
                    ref="menu"
                    v-model="menu"
                    :close-on-content-click="false"
                    :return-value.sync="searchParams.start_date"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template v-slot:activator="{ on, attrs }">
                      <v-text-field
                        v-model="searchParams.start_date"
                        label="Departure Date"
                        outlined
                        dense
                        hide-details
                        prepend-inner-icon="mdi-calendar"
                        class="mb-3"
                       
                        readonly
                        v-bind="attrs"
                        v-on="on"
                      ></v-text-field>
                    </template>
                    <v-date-picker
                      v-model="searchParams.start_date"
                      @change="fetchFlights"
                      no-title
                      scrollable
                    >
                      <v-spacer></v-spacer>
                      <v-btn
                        text
                        color="primary"
                        @click="menu = false"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        text
                        color="primary"
                        @click="$refs.menu.save(searchParams.start_date)"
                      >
                        OK
                      </v-btn>
                    </v-date-picker>
                  </v-menu>
                </v-col>

                <v-col cols="6">
                  <v-select
                    v-model="searchParams.cabin_class"
                    :items="cabinOptions"
                    label="Cabin Class"
                    outlined
                    dense
                    hide-details
                    @change="fetchFlights"
                    prepend-inner-icon="mdi-sofa-single"
                    class="mb-3"
                  ></v-select>
                </v-col>
  
                <!-- Passenger Counts -->
                <v-col cols="4">
                  <v-select
                    v-model="searchParams.adults"
                    :items="[1,2,3,4,5,6,7,8,9,10]"
                    label="Adults"
                    outlined
                    dense
                    @change="fetchFlights"
                  ></v-select>
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="searchParams.children"
                    :items="[0,1,2,3,4,5,6,7,8,9,10]"
                    label="Children"
                    outlined
                    dense
                    @change="fetchFlights"
                  ></v-select>
                </v-col>
                <v-col cols="4">
                  <v-select
                    v-model="searchParams.infants"
                    :items="[0,1,2,3,4,5,6,7,8,9,10]"
                    label="Infants"
                    outlined
                    dense
                    @change="fetchFlights"
                  ></v-select>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
  
          <!-- Loading State -->
          <v-layout v-if="loading" column align-center justify-center class="py-10">
            <v-progress-circular indeterminate color="teal" size="48" class="mb-2"></v-progress-circular>
            <span class="grey--text text--darken-1 subtitle-2">Searching live vendor fares...</span>
          </v-layout>
  
          <!-- Error Alert -->
          <v-alert v-if="error" type="error" dismissible class="mb-4">
            {{ error }}
          </v-alert>
  
          <!-- Flights List (Ordered Chronologically Day by Day) -->
          <div v-if="!loading && processedDays.length">
            <div >
              <v-expansion-panels :value="0">
              <v-expansion-panel
              v-for="day in processedDays" :key="day.date" class="mb-6"
              >
                <v-expansion-panel-header>
                  <!-- Date Section Header -->
              <v-subheader class="font-weight-bold teal--text text--darken-3 pl-1 subtitle-1">
                <v-icon left color="teal">mdi-calendar</v-icon>
                Flights on {{ formatDate(day.date) }} 
              </v-subheader>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <!-- Flights Sub-sorted Cheapest First -->
              <div v-for="(flight, idx) in day.flights" 
                :key="idx" >

              <FlightDataCard :flight="flight" :searchParams="searchParams" @open-booking-gate="openBookingGate"></FlightDataCard>

              </div>

              <!-- No Flights Found State -->
              <v-card v-if="day.status !== 'success' || !day.flights.length" class="pa-4 text-center rounded-lg" outlined>
                <v-icon color="grey lighten-1" size="36">mdi-calendar-remove</v-icon>
                <div class="grey--text text--darken-1 subtitle-2 mt-1">
                  We are gathering fresh flight options in the background. Please refresh your browser now or try again in 5 minutes.
                </div>
              </v-card>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>
              
              
  
             
             
  
            </div>
          </div>
  
        </v-container>
      </v-main>
  
      <!-- Passcode Agent Security Dialog -->
      <v-dialog v-model="passcodeDialog" max-width="340px" persistent rounded="lg">
        <v-card class="pa-2">
          <v-card-title class="headline font-weight-bold text-center justify-center pt-4">
            <v-icon large color="teal" class="mb-2">mdi-shield-key-outline</v-icon>
            <div>Agent Authorization</div>
          </v-card-title>
          
          <v-card-text class="text-center pb-0">
            <p class="grey--text text--darken-1 body-2">
              Pricing is open to public display. However, booking ticketing actions require an active agent passcode block validation.
            </p>
            <v-text-field
              v-model="passcode"
              label="Enter 7-Digit Passcode"
              type="password"
              outlined
              dense
              color="teal"
              maxlength="7"
              :error-messages="passcodeError"
              @input="passcodeError = ''"
              v-on:keyup.enter="submitPasscode"
            ></v-text-field>
          </v-card-text>
  
          <v-card-actions class="pb-4 px-4 justify-space-between">
            <v-btn text color="grey darken-1" @click="closePasscodeDialog">Cancel</v-btn>
            <v-btn color="teal" dark class="px-6 rounded-lg" :loading="checkingPasscode" @click="submitPasscode">
              Verify
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-app>
  </template>
  
  <script>
  import axios from '@/plugins/axios';
  import flightDataSampleResponse from "./sampleReturnData.json"
  import FlightDataCard from "./FlightDataCard.vue"

  export default {
    name: 'TicketToGhana',
    components:{FlightDataCard},
    updated() {
    const isMobile = this.$vuetify.breakpoint.mobile;
    this.$store.commit("setIsMobile", isMobile);
  },
    mounted(){
      const isMobile = this.$vuetify.breakpoint.mobile;
      this.$store.commit("setIsMobile", isMobile);
         const today = new Date();
         this.searchParams.start_date = today.toISOString().substring(0, 10);
        this.fetchFlights()
    },
    data: function() {
      return {
        menu: false,
        loading: false,
        error: null,
        passcodeDialog: false,
        checkingPasscode: false,
        passcode: '',
        passcodeError: '',
        selectedFlight: null,
        itineraries:[],
        carriers:[],
        cabinOptions: [
          { text: 'Economy Class', value: 'Economy' },
          { text: 'Business Class', value: 'Business' },
          { text: 'First Class', value: 'First' }
        ],
        origins:[
            // --- South Africa ---
            { text: "Johannesburg O.R. Tambo (JNB)", value: "JNB" },
            { text: "Durban King Shaka (DUR)", value: "DUR" },
            { text: "Cape Town Airport (CPT)", value: "CPT" },
            { text: "Johannesburg Lanseria (HLA)", value: "HLA" },
            { text: "Gqeberha Chief Dawid Stuurman (PLZ)", value: "PLZ" },
            { text: "Kruger Mpumalanga International (MQP)", value: "MQP" },
            { text: "Bloemfontein Bram Fischer (BFN)", value: "BFN" },
            { text: "East London King Phalo (ELS)", value: "ELS" },
            { text: "George Airport (GRJ)", value: "GRJ" },
            { text: "Polokwane International (PTG)", value: "PTG" },
            { text: "Hoedspruit Eastgate (HDS)", value: "HDS" },
            { text: "Kimberley Airport (KIM)", value: "KIM" },
            { text: "Mthatha Airport (UTT)", value: "UTT" },
            { text: "Richards Bay Airport (RCB)", value: "RCB" },
            { text: "Skukuza Airport (SZK)", value: "SZK" },
            { text: "Upington Airport (UPN)", value: "UPN" },
            { text: "Sishen Airport (SIS)", value: "SIS" },
            { text: "Margate Airport (MGH)", value: "MGH" },
            { text: "Phalaborwa Airport (PHW)", value: "PHW" },
            { text: "Plettenberg Bay Airport (PBZ)", value: "PBZ" },
            // --- Ghana ---
            { text: "Accra Kotoka (ACC)", value: "ACC" },
            { text: "Kumasi International (KMS)", value: "KMS" },
            { text: "Tamale International (TML)", value: "TML" },
            { text: "Takoradi Airport (TKD)", value: "TKD" },
            { text: "Sunyani Airport (NYI)", value: "NYI" },
            { text: "Wa Airport (WST)", value: "WST" }
],
        searchParams: {
          origin:"JNB",
          destination:"ACC",
          currency: "ZAR",
          adults: 1,
          children: 0,
          infants: 0,
          cabin_class: 'Economy',
          start_date: new Date().toISOString().substring(0, 10)
        },
        rawResponseData: {}
      };
    },
    computed: {
        getApiUrl(){
            return this.$store.getters.getApiUrl
        },
        isMobile(){
            return this.$store.getters.isMobile
        },
      // Requirements 1 & 2: Chronological dates & cheapest flight sorted inside frontend computation blocks
      processedDays: function() {
        const vm = this;
        if (!vm.rawResponseData) return [];

        const sortedDates = Object.keys(vm.rawResponseData).sort();
        
      return sortedDates.map((dateString) => {
        
        let extractedFlights = [];
        let itineraries = null;
        let carriersMap = {};
        let segmentsMap = {};

        //database data vs api
        let dayInfo = vm.rawResponseData[dateString];
        dayInfo = typeof dayInfo === 'string' ? JSON.parse(dayInfo) : dayInfo;
        
        if(dayInfo && dayInfo.data){
          dayInfo = dayInfo.data
        }
        
        if (dayInfo) {
          itineraries = dayInfo.itineraries ? dayInfo.itineraries : (dayInfo.payload ? dayInfo.payload.itineraries : null);
          carriersMap = dayInfo.carriers ? dayInfo.carriers : (dayInfo.payload ? dayInfo.payload.carriers : {});
          segmentsMap = dayInfo.segments ? dayInfo.segments : (dayInfo.payload ? dayInfo.payload.segments : {});
        }

        if (itineraries && Array.isArray(itineraries)) {

          extractedFlights = itineraries.map((itinerary) => {
            // console.log("itinerary: ",itinerary)
            let priceVal = 0;
            let bookingUrl = '#';
            let carrierName = 'Unknown Carrier';
            let carrierIata = '---';
            let departureDate = '';
            let departureTime = '';
            let arrivalDate = '';
            let arrivalTime = '';
            let stopCount = 0;
            let flightNumber = '';

            let primaryOption = null;
            if (itinerary.pricing_options && itinerary.pricing_options[0]) {
              primaryOption = itinerary.pricing_options[0];
              
              if (primaryOption.price && primaryOption.price.amount) {
                priceVal = parseFloat(primaryOption.price.amount);
              }

              if (primaryOption.items && primaryOption.items[0] && primaryOption.items[0].url) {
                const relativeUrl = primaryOption.items[0].url;
                const cleanPath = relativeUrl.indexOf('/') === 0 ? relativeUrl : '/' + relativeUrl;
                bookingUrl = 'https://www.skyscanner.com' + cleanPath;
              }
            }

            // Helper strategy to query carriers database reliably using flexible type variants
            const getCarrierMeta = (id) => {
              if (!id) return null;
              const idStr = id.toString();
              if (carriersMap[idStr]) return carriersMap[idStr];
              
              // Match keys by handling sign variations (e.g., -32139 vs 32139)
              if (idStr.indexOf('-') === 0 && carriersMap[idStr.substring(1)]) return carriersMap[idStr.substring(1)];
              if (idStr.indexOf('-') === -1 && carriersMap['-' + idStr]) return carriersMap['-' + idStr];
              
              return null;
            };

            if (primaryOption && primaryOption.items && primaryOption.items[0]) {
              const item = primaryOption.items[0];
              const segmentIds = item.segment_ids;
              
              if (segmentIds && Array.isArray(segmentIds)) {
                stopCount = segmentIds.length - 1 < 0 ? 0 : segmentIds.length - 1;

                const findSegmentById = (id) => {
                  if (!id) return null;
                  if (segmentsMap[id]) return segmentsMap[id];
                  if (Array.isArray(segmentsMap)) {
                    for (let i = 0; i < segmentsMap.length; i++) {
                      if (segmentsMap[i] && segmentsMap[i].id === id) {
                        return segmentsMap[i];
                      }
                    }
                  }
                  return null;
                };

                const firstSegObj = findSegmentById(segmentIds[0]);
                
                if (firstSegObj) {
                  const marketingCarrierId = firstSegObj.marketing_carrier_id ? firstSegObj.marketing_carrier_id : firstSegObj.carrier;
                  const carrierMeta = getCarrierMeta(marketingCarrierId);
                  
                  if (carrierMeta) {
                    carrierName = carrierMeta.name ? carrierMeta.name : carrierName;
                    carrierIata = carrierMeta.iata ? carrierMeta.iata : carrierIata;
                  }

                  const depVal = firstSegObj.departure ? firstSegObj.departure : (firstSegObj.departureDateTime ? firstSegObj.departureDateTime : '');
                  if (depVal) {
                    const depSplit = depVal.split('T');
                    departureDate = depSplit[0];
                    departureTime = depSplit[1] ? depSplit[1].substring(0, 5) : '';
                  }

                  const numVal = firstSegObj.marketing_flight_number ? firstSegObj.marketing_flight_number : (firstSegObj.flightNumber ? firstSegObj.flightNumber : firstSegObj.number);
                  if (numVal) {
                    const resolvedIata = carrierIata !== '---' ? carrierIata : 'FL';
                    flightNumber = isNaN(numVal) ? numVal : resolvedIata + numVal;
                  }
                }

                const lastSegObj = findSegmentById(segmentIds[segmentIds.length - 1]);
                if (lastSegObj) {
                  const arrVal = lastSegObj.arrival ? lastSegObj.arrival : (lastSegObj.arrivalDateTime ? lastSegObj.arrivalDateTime : '');
                  if (arrVal) {
                    const arrSplit = arrVal.split('T');
                    arrivalDate = arrSplit[0];
                    arrivalTime = arrSplit[1] ? arrSplit[1].substring(0, 5) : '';
                  }
                }
              }

              // Fallback parsing strategy using item metadata arrays if segment targets failed
              if (carrierName === 'Unknown Carrier' && item.marketing_carrier_ids && item.marketing_carrier_ids[0]) {
                const carrierMeta = getCarrierMeta(item.marketing_carrier_ids[0]);
                if (carrierMeta) {
                  carrierName = carrierMeta.name ? carrierMeta.name : carrierName;
                  carrierIata = carrierMeta.iata ? carrierMeta.iata : carrierIata;
                }
              }

              // Final deep-link text fallback strategy block 
              if ((!flightNumber || !departureDate || carrierName === 'Unknown Carrier') && item.url) {
                const urlString = item.url;
                const itMatches = urlString.match(/itinerary=flight\|([^&]+)/);
                
                if (itMatches && itMatches[1]) {
                  const dateTimeMatches = itMatches[1].match(/\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/g);
                  
                  if (dateTimeMatches && dateTimeMatches.length >= 2) {
                    if (!departureDate) {
                      const depSplit = dateTimeMatches[0].split('T');
                      departureDate = depSplit[0];
                      departureTime = depSplit[1];
                    }
                    if (!arrivalDate) {
                      const arrSplit = dateTimeMatches[dateTimeMatches.length - 1].split('T');
                      arrivalDate = arrSplit[0];
                      arrivalTime = arrSplit[1];
                    }
                  }

                  const segments = itMatches[1].split(';');
                  const firstSegmentParts = segments[0].split('|');
                  
                  if (firstSegmentParts[1]) {
                    const carrierMeta = getCarrierMeta(firstSegmentParts[1]);
                    if (carrierMeta) {
                      if (carrierName === 'Unknown Carrier') carrierName = carrierMeta.name ? carrierMeta.name : carrierName;
                      if (carrierIata === '---') carrierIata = carrierMeta.iata ? carrierMeta.iata : carrierIata;
                    }
                    
                    if (!flightNumber && firstSegmentParts[2]) {
                      const resolvedIata = carrierIata !== '---' ? carrierIata : 'FL';
                      flightNumber = resolvedIata + firstSegmentParts[2];
                    }
                  }
                }
              }
            }

            // Ultimate hard backup strategy - pull code key indicators out of stringified itinerary ids
            if (carrierName === 'Unknown Carrier' && itinerary.id) {
              const parts = itinerary.id.split('-');
              for (let i = 0; i < parts.length; i++) {
                const carrierMeta = getCarrierMeta(parts[i]);
                if (carrierMeta) {
                  carrierName = carrierMeta.name ? carrierMeta.name : carrierName;
                  carrierIata = carrierMeta.iata ? carrierMeta.iata : carrierIata;
                  break;
                }
              }
            }
            const profit = this.getFlightProfit(this.searchParams.origin, this.searchParams.destination)
            return {
              carrier_name: carrierName,
              carrier_iata: carrierIata,
              price: priceVal + profit,
              departure_date: departureDate,
              departure_time: departureTime,
              arrival_date: arrivalDate,
              arrival_time: arrivalTime,
              stop_counts: stopCount,
              flight_number: flightNumber ? flightNumber : 'N/A',
              bookingUrl: bookingUrl,
              rawTrip: itinerary
            };
          });

          extractedFlights.sort((a, b) => {
            return a.price - b.price;
          });
        }

        return {
          date: dateString,
          status: dayInfo ? (dayInfo.status ? dayInfo.status : 'success') : 'error',
          flights: extractedFlights
        };
      });
}


    },
    methods: {
      getFlightProfit(origin, destination) {
        if(!origin || !destination) return 0;
        let profit = 0;

  // Define airport sets for each country
  const southAfricaAirports = new Set([
    "JNB", "DUR", "CPT", "HLA", "PLZ", "MQP", "BFN", "ELS", "GRJ", 
    "PTG", "HDS", "KIM", "UTT", "RCB", "SZK", "UPN", "SIS", "MGH", "PHW", "PBZ"
  ]);

  const ghanaAirports = new Set([
    "ACC", "KMS", "TML", "TKD", "NYI", "WST"
  ]);

  // Determine country of origin and destination
  const isOriginSA = southAfricaAirports.has(origin);
  const isOriginGhana = ghanaAirports.has(origin);
  
  const isDestSA = southAfricaAirports.has(destination);
  const isDestGhana = ghanaAirports.has(destination);

  // Apply pricing logic based on routes
  if (isOriginSA && isDestSA) {
    profit = 250; // Domestic South Africa
  } else if (isOriginGhana && isDestGhana) {
    profit = 0; // Domestic Ghana
  } else if ((isOriginSA && isDestGhana) || (isOriginGhana && isDestSA)) {
    profit = 1000; // Regional between SA and Ghana
  }

  return profit;
}
,
      fetchFlights() {
        // this.rawResponseData = flightDataSampleResponse
        // console.log("fetch: ",this.searchParams)
        // return

        // 1. Get today's date and completely clear out the hours/minutes/seconds
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // 2. Convert your input string (e.g., "2026-07-07") into a clean Date object at midnight
        const selectedDate = new Date(this.searchParams.start_date);
        selectedDate.setHours(0, 0, 0, 0);

        // 3. Run a strict check: must have a date selected, AND it must be strictly before today
        if (!this.searchParams.start_date || selectedDate < today) {
          this.$toast.error("Departure Date Cannot be in the Past! Please Select a Valid Date.");
          return; // Instantly halts execution of the rest of your method
        }
        
         this.loading = true;
         this.error = null;
        
        axios.postData(this.getApiUrl + '/search-flights-range', this.searchParams)
          .then((response) => {
            this.rawResponseData = this.processResponseAndGetItineraries(response);
          
            this.loading = false;
          })
          .catch((err) => {
            this.error = 'Failed fetching live airline arrays. Confirm backend connectivity profiles.';
            this.loading = false;
            console.error(err);
          });
      },
      processResponseAndGetItineraries(response){
          if(!response) return null;

          const validData = {}

          const departureDates = Object.entries(response)
          if(departureDates.length < 1) return null;

          departureDates.forEach(([key, value]) => {
          if (!key || !value) {
            return;
          }

            let dayInfo = typeof value === 'string' ? JSON.parse(value) : value;
              
            if(dayInfo && dayInfo.status && (dayInfo.status == "error" || dayInfo.status == "status") ){
                return;
              }

              if(dayInfo && dayInfo.data){
                dayInfo = dayInfo.data
              }
            validData[key] = dayInfo
          })
          return validData
      },
      openBookingGate(flight) {
        this.selectedFlight = flight;
        this.passcode = '';
        this.passcodeError = '';
        this.passcodeDialog = true;
      },
      closePasscodeDialog() {
        this.passcodeDialog = false;
        this.selectedFlight = null;
      },
      submitPasscode() {
        if (!this.passcode) {
          this.passcodeError = 'Passcode required';
          return;
        }
  
        this.checkingPasscode = true;
        
        // Requirement 5 & 6: Validate against backend endpoint securely
        axios.postData(this.getApiUrl+ '/verify-passcode', { passcode: this.passcode })
          .then((res) => {
            this.checkingPasscode = false;
            if (res && res.status === "success") {
              this.passcodeDialog = false;
              
              // Success hook route: extract booking links target inside payload to exit boundary
              if (this.selectedFlight && this.selectedFlight.bookingUrl && this.selectedFlight.bookingUrl !== '#') {
                window.open(this.selectedFlight.bookingUrl, '_blank');
              } else {
                alert('Passcode accepted! Redirecting to global vendor checkout portal window context.');
              }
            }
          })
          .catch(()=>{
            this.checkingPasscode = false;
            this.passcodeError = 'Access Denied: Invalid Agent Token';
          });
      },
      formatDate: function(dateStr) {
        if (!dateStr) return '';
        var parts = dateStr.split('-');
        if (parts.length !== 3) return dateStr;
        // Readable format conversion: 2026-10-10 -> 10 Oct 2026
        var months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        return parts[2] + ' ' + months[parseInt(parts[1], 10) - 1] + ' ' + parts[0];
      },
      
    },
    
  };
  </script>
  
  <style scoped>
  .border-teal-left {
    border-left: 5px solid #062f2b !important;
  }
  .bg-light-grey {
    background-color: #f9f9f9;
  }
  </style>